import { useState } from 'react';
import { shiftsAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Copy, ArrowRight } from 'lucide-react';
import { format, startOfWeek, addDays, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function CopyWeekDialog({ open, onClose, sourceWeekStart, onCopied }) {
  const [targetDate, setTargetDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const sourceWeekNumber = getISOWeek(sourceWeekStart);
  const sourceEnd = addDays(sourceWeekStart, 6);

  const targetWeekStart = targetDate ? startOfWeek(targetDate, { weekStartsOn: 1 }) : null;
  const targetWeekNumber = targetWeekStart ? getISOWeek(targetWeekStart) : null;
  const targetEnd = targetWeekStart ? addDays(targetWeekStart, 6) : null;

  const isSameWeek = targetWeekStart && 
    format(targetWeekStart, 'yyyy-MM-dd') === format(sourceWeekStart, 'yyyy-MM-dd');

  const handleCopy = async () => {
    if (!targetWeekStart || isSameWeek) return;
    setLoading(true);
    try {
      const res = await shiftsAPI.copyWeek({
        source_week_start: format(sourceWeekStart, 'yyyy-MM-dd'),
        target_week_start: format(targetWeekStart, 'yyyy-MM-dd'),
      });
      toast.success(res.data.message);
      onCopied(targetWeekStart);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la copie');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setTargetDate(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="copy-week-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5" strokeWidth={1.5} />
            Copier le planning
          </DialogTitle>
          <DialogDescription>
            Copier tous les shifts de la semaine {sourceWeekNumber} ({format(sourceWeekStart, 'd MMM', { locale: fr })} - {format(sourceEnd, 'd MMM', { locale: fr })}) vers une autre semaine
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          {/* Source week info */}
          <div className="flex items-center gap-3 justify-center text-sm">
            <div className="bg-slate-100 rounded-lg px-3 py-2 text-center">
              <div className="text-xs text-slate-500 mb-0.5">Source</div>
              <div className="font-semibold font-data">S. {sourceWeekNumber}</div>
              <div className="text-xs text-slate-400 font-data">
                {format(sourceWeekStart, 'dd/MM', { locale: fr })} - {format(sourceEnd, 'dd/MM', { locale: fr })}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
            <div className={`rounded-lg px-3 py-2 text-center ${targetWeekStart && !isSameWeek ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-dashed border-slate-300'}`}>
              <div className="text-xs text-slate-500 mb-0.5">Destination</div>
              {targetWeekStart && !isSameWeek ? (
                <>
                  <div className="font-semibold font-data text-blue-900">S. {targetWeekNumber}</div>
                  <div className="text-xs text-blue-700 font-data">
                    {format(targetWeekStart, 'dd/MM', { locale: fr })} - {format(targetEnd, 'dd/MM', { locale: fr })}
                  </div>
                </>
              ) : (
                <div className="text-xs text-slate-400">Choisir ci-dessous</div>
              )}
            </div>
          </div>

          {/* Calendar picker */}
          <div className="flex justify-center">
            <Calendar
              data-testid="copy-week-calendar"
              mode="single"
              selected={targetDate}
              onSelect={setTargetDate}
              locale={fr}
              weekStartsOn={1}
              className="rounded-lg border border-slate-200"
            />
          </div>

          {isSameWeek && targetDate && (
            <p className="text-sm text-amber-600 text-center font-medium">
              Vous ne pouvez pas copier vers la même semaine
            </p>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="copy-week-cancel-btn" variant="outline" onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            data-testid="copy-week-confirm-btn"
            onClick={handleCopy}
            disabled={loading || !targetWeekStart || isSameWeek}
            className="gap-2"
          >
            <Copy className="w-4 h-4" strokeWidth={1.5} />
            {loading ? 'Copie en cours...' : 'Copier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
